// ** React Imports
import { useState, useEffect, useMemo } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Grid,
  Popover,
  IconButton,
  useTheme
} from "@mui/material";

// ** Icon Imports
import Icon from "components/icon";

// ** Component Imports
import Bundle from "./Bundle";
import Service from "./Service";
import JobScheduleDialog from "screens/WorkOrders/component/JobSchedule";
import AddServiceDialog from "screens/WorkOrders/component/AddService";
import BulkScheduleDialog from "screens/WorkOrders/component/BulkScheduleDialog";
import { formatUSCurrency } from "components/CurrencyFormat";

import TotalOverviewDialog from "screens/Estimates/component/TotalOverview";
import { useTotalOverviewLogic } from "screens/Estimates/logic/total-overview-logic";

const dropdownItemsStyle = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  px: 4,
  py: 2,
  "&:hover": {
    backgroundColor: "action.hover",
    cursor: "pointer",
  },
};

// Custom Styled Button
const PopoverButton = ({ handleOpenServiceDlg, handleOpenBundleDlg, rules }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <Button
        variant="contained"
        color="main"
        endIcon={<Icon icon="tabler:chevron-down" />}
        aria-describedby={id}
        onClick={handleClick}
        disabled={!rules?.canEdit}
      >
        Add Services
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2, minWidth: 160 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={dropdownItemsStyle} onClick={() => { 
                handleOpenBundleDlg()
                handleClose()
              }}>
              <Typography variant="h6">Bundle</Typography>
            </Box>
            <Box sx={dropdownItemsStyle} onClick={() => {
              handleOpenServiceDlg()
              handleClose()
              }}>
              <Typography variant="h6">Service</Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

const ServiceOptions = () => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <IconButton onClick={handleClick}>
        <Icon icon="tabler:dots-vertical" />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={dropdownItemsStyle}>
              View Request
            </Typography>
            <Typography variant="h6" sx={dropdownItemsStyle}>
              View Assessment
            </Typography>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

const WorkRequirements = ({
  entity,
  method,
  handleOpenServiceDlg,
  handleOpenBundleDlg,
  handleWorkRequirementToggle,
  handleToggleBundle,
  workRequirementList,
  openService,
  openBundle,
  handleAddInstruction,
  handleRemoveInstruction,
  handleToggleAlert,
  newInstruction,
  setNewInstruction,
  handleWorkRequirementChange,
  handleChange,
  UpdateWorkRequirement,
  PropertyAreas,
  fetchPropertyAreas,
  handleOpenWorkRequirementUploadDlg,
  handleUploadAttachment,
  showConfirmationAttachmentDelete,
  fetchAttachments,
  handleDownloadAttachment,
  attachmentMap,  
  showConfirmationWorkRequirementDelete,
  showConfirmationBundleDelete,
  rules,  
  setLoading,
  fetchWorkRequirements,
  requestID,
  toast,
  LineItemTotalProps,
  fetchRequests,
}) => {
  const theme = useTheme();
  const [openJobSchedule, setOpenJobSchedule] = useState(false);
  const [openScheduleServiceDlg, setOpenScheduleServiceDlg] = useState(false);
  const [openBulkSchedule, setOpenBulkSchedule] = useState(false);
  const [selectedToSchedule, setSelectedToSchedule] = useState([]);
  const [lineItemChanged, setLineItemChanged] = useState(false);

  const handleOpenJobSchedule = () => setOpenJobSchedule(true);
  const handleCloseJobSchedule = () => setOpenJobSchedule(false);  

  const handleOpenScheduleServiceDlg = () => setOpenScheduleServiceDlg(true);
  const handleCloseScheduleServiceDlg = () => setOpenScheduleServiceDlg(false); 
  
  const allServicesScheduled =
  Array.isArray(workRequirementList) &&
  workRequirementList.length > 0 &&
  workRequirementList.every(svc =>
    (svc.jobs ?? []).some(job => job.occurrenceNo > 0)
  );

  const tileStyles = {
    px: 6,
    py: 2,
    backgroundColor: "#003A7014",
    color: 
      theme.palette.mode === 'dark' ? 
        theme.palette.info.light : "#003A70",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.12)",
    textAlign: "center",
    borderRadius: 1,
      width: {
    xs: 280, 
    sm: 380,
    md: 380,
    lg: 160     
  },

  };

  const { 
    budgettedSummary,
    getBudgetedSummary
  } = LineItemTotalProps;

  useEffect(() => {    
    getBudgetedSummary(0, 0,requestID);
  }, [requestID, workRequirementList, lineItemChanged]);

  const bundleServices = useMemo(
    () => workRequirementList.filter(x => x.bundleID > 0),
    [workRequirementList]
  ); 
  const standaloneServices = useMemo(
    () => workRequirementList.filter(x => x.bundleID === 0 && x.subServiceID > 0),
    [workRequirementList]
  );

  const { TotalOverviewProps } = useTotalOverviewLogic(requestID, setLoading, toast);
  const {
    handleOpenTotalOverview
  } = TotalOverviewProps;

  return (
    <>
      {entity === "estimate-detail" ||
      entity === "workOrder" ||
      entity === "workOrder-schedule" ||
      entity === "workOrder-detail" ? (
        <Grid item xs={12}>
          {entity === "workOrder-schedule" ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {console.log(!rules.canEdit)}
              {console.log(workRequirementList)}
              {console.log(allServicesScheduled)}
              <Button
                variant="outlined"
                color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                startIcon={<Icon icon="tabler:calendar" />}
                onClick={() => setOpenBulkSchedule(true)}
                disabled={
                  !rules.canEdit ||
                  (workRequirementList && workRequirementList.length === 0) ||
                  allServicesScheduled
                }
              >
                Bulk Schedule
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" }, 
                alignItems: { xs: "center", lg: "center" },
                justifyContent: "space-between", 
                gap: 2,
                width: "100%",
              }}
            >
              {/* Tiles Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: { xs: 2, lg: 4 },
                  alignItems: { xs: "flex-start", lg: "flex-start" },
                 
                }}
              >
                <Box sx={{ ...tileStyles }}>
                  <Typography variant="body2" color='inherit'>
                    Your Cost
                  </Typography>
                  <Typography variant="body1" color='inherit'>
                    {formatUSCurrency(budgettedSummary[0]?.totalCost)}
                  </Typography>
                </Box>
                <Box sx={{ ...tileStyles }}>
                  <Typography variant="body2" color="inherit">
                    Customer's Price
                  </Typography>
                  <Typography variant="body1" color="inherit">
                    {formatUSCurrency(budgettedSummary[0]?.totalPrice)}
                  </Typography>
                </Box>
                <Box sx={{ ...tileStyles }}>
                  <Typography variant="body2" color="inherit">
                    Manhours
                  </Typography>
                  <Typography variant="body1" color="inherit">
                    {budgettedSummary[0]?.totalManHours || 0}h
                  </Typography>
                </Box>
                <Box
                  sx={{
                    ...tileStyles,
                    bgcolor: "rgba(35, 162, 109, 0.15) !important",
                  }}
                >
                  <Typography variant="body2" color="#23A26D">
                    Total Profit
                  </Typography>
                  <Typography variant="body1" color="#23A26D">
                    {formatUSCurrency(budgettedSummary[0]?.totalProfit)}
                  </Typography>
                </Box>
              </Box>
              {/* Buttons Section */}
               <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 2,
                  alignItems: { xs: "center", lg: "center" },
                  justifyContent: { xs: "center", lg: "flex-end" },
                  width: "100%",
                }}
              >

                {entity === "estimate-detail" && (
                  <Button
                    variant="outlined"
                    color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                    sx={{ width: { xs: "100%", lg: "auto" } }} 
                    onClick={() => handleOpenTotalOverview(requestID)}
                  >
                    Totals Overview
                  </Button>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {entity !== "workOrder-schedule" && (
                    <PopoverButton
                      handleOpenServiceDlg={handleOpenServiceDlg}
                      handleOpenBundleDlg={handleOpenBundleDlg}
                      rules={rules}
                    />
                  )}

                  <ServiceOptions />
                </Box>
              </Box>
            </Box>
          )}
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <PopoverButton
              handleOpenServiceDlg={handleOpenServiceDlg}
              handleOpenBundleDlg={handleOpenBundleDlg}
              rules={rules}
            />
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        {/* {(entity === "workOrder" || entity === 'workOrder-schedule' || entity === 'workOrder-detail') ? (
          <Service entity={entity} 
            method={method}
            workRequirementList={workRequirementList.filter(x => x.subServiceID > 0)}
            openService={openService}
            handleWorkRequirementToggle={handleWorkRequirementToggle}
            handleAddInstruction={handleAddInstruction}
            handleRemoveInstruction={handleRemoveInstruction}
            handleToggleAlert={handleToggleAlert}
            newInstruction={newInstruction}
            setNewInstruction={setNewInstruction}
            handleWorkRequirementChange={handleWorkRequirementChange}
            handleChange={handleChange}
            UpdateWorkRequirement={UpdateWorkRequirement}
            PropertyAreas={PropertyAreas}
            handleOpenWorkRequirementUploadDlg={handleOpenWorkRequirementUploadDlg} 
            handleUploadAttachment={handleUploadAttachment}
            showConfirmationAttachmentDelete={showConfirmationAttachmentDelete}
            fetchAttachments={fetchAttachments}
            handleDownloadAttachment={handleDownloadAttachment}
            attachmentMap={attachmentMap}  
            showConfirmationWorkRequirementDelete={showConfirmationWorkRequirementDelete}
            rules={rules}
            setLoading={setLoading} 
            toast={toast}
            bundle={false}
            handleOpenScheduleServiceDlg={handleOpenScheduleServiceDlg}
            fetchWorkRequirements={fetchWorkRequirements}
            requestID={requestID}
            
          />
        ) : (
          <> */}
        <Bundle
          entity={entity}
          method={method}
          workRequirementList={bundleServices}
          openBundle={openBundle}
          openService={openService}
          handleToggleBundle={handleToggleBundle}
          handleWorkRequirementToggle={handleWorkRequirementToggle}
          handleAddInstruction={handleAddInstruction}
          handleRemoveInstruction={handleRemoveInstruction}
          handleToggleAlert={handleToggleAlert}
          newInstruction={newInstruction}
          setNewInstruction={setNewInstruction}
          handleWorkRequirementChange={handleWorkRequirementChange}
          handleChange={handleChange}
          UpdateWorkRequirement={UpdateWorkRequirement}
          PropertyAreas={PropertyAreas}
          fetchPropertyAreas={fetchPropertyAreas}
          handleOpenWorkRequirementUploadDlg={
            handleOpenWorkRequirementUploadDlg
          }
          handleUploadAttachment={handleUploadAttachment}
          showConfirmationAttachmentDelete={showConfirmationAttachmentDelete}
          fetchAttachments={fetchAttachments}
          handleDownloadAttachment={handleDownloadAttachment}
          attachmentMap={attachmentMap}
          showConfirmationWorkRequirementDelete={
            showConfirmationWorkRequirementDelete
          }
          showConfirmationBundleDelete={showConfirmationBundleDelete}
          rules={rules}
          setLoading={setLoading}
          toast={toast}
          bundle={true}
          handleOpenScheduleServiceDlg={handleOpenScheduleServiceDlg}
          fetchWorkRequirements={fetchWorkRequirements}
          requestID={requestID}
          LineItemTotalProps={LineItemTotalProps}
          fetchRequests={fetchRequests}
          lineItemChanged={lineItemChanged}
          setLineItemChanged={setLineItemChanged}
        />
        <Service
          entity={entity}
          method={method}
          workRequirementList={standaloneServices}
          openService={openService}
          handleWorkRequirementToggle={handleWorkRequirementToggle}
          handleAddInstruction={handleAddInstruction}
          handleRemoveInstruction={handleRemoveInstruction}
          handleToggleAlert={handleToggleAlert}
          newInstruction={newInstruction}
          setNewInstruction={setNewInstruction}
          handleWorkRequirementChange={handleWorkRequirementChange}
          handleChange={handleChange}
          UpdateWorkRequirement={UpdateWorkRequirement}
          PropertyAreas={PropertyAreas}
          fetchPropertyAreas={fetchPropertyAreas}
          handleOpenWorkRequirementUploadDlg={
            handleOpenWorkRequirementUploadDlg
          }
          handleUploadAttachment={handleUploadAttachment}
          showConfirmationAttachmentDelete={showConfirmationAttachmentDelete}
          fetchAttachments={fetchAttachments}
          handleDownloadAttachment={handleDownloadAttachment}
          attachmentMap={attachmentMap}
          showConfirmationWorkRequirementDelete={
            showConfirmationWorkRequirementDelete
          }
          rules={rules}
          setLoading={setLoading}
          toast={toast}
          bundle={false}
          fetchWorkRequirements={fetchWorkRequirements}
          requestID={requestID}
          handleOpenScheduleServiceDlg={handleOpenScheduleServiceDlg}
          LineItemTotalProps={LineItemTotalProps}
          fetchRequests={fetchRequests}
          lineItemChanged={lineItemChanged}
          setLineItemChanged={setLineItemChanged}
        />
        {/* </>
        )} */}
      </Grid>

      <TotalOverviewDialog 
        TotalOverviewProps={TotalOverviewProps}
      />

      <AddServiceDialog
        open={openScheduleServiceDlg}
        handleCloseDlg={handleCloseScheduleServiceDlg}
      />

      <BulkScheduleDialog
        open={openBulkSchedule}
        options={workRequirementList}
        selected={selectedToSchedule}
        onChange={setSelectedToSchedule}
        onClose={() => setOpenBulkSchedule(false)}
        onNext={() => {
          setOpenBulkSchedule(false);
          setOpenJobSchedule(true);
        }}
      />

      {/* <JobScheduleDialog 
        open={openJobSchedule}
        handleClose={handleCloseJobSchedule}
      /> */}
      <JobScheduleDialog
        open={openJobSchedule}
        services={selectedToSchedule}
        handleClose={handleCloseJobSchedule}
        fetchWorkRequirements={fetchWorkRequirements}
        requestID={requestID}
        toast={toast}
        onBack={() => {
          setOpenJobSchedule(false);
          setOpenBulkSchedule(true);
        }}
      />
    </>
  );
};

export default WorkRequirements;
